// src/pages/Administration/Members/index.tsx
/**
 * `/Administration/Members`: find a person, check their details, correct what is wrong.
 *
 * Built from `@/components/ui` and shaped like `/Administration/Users` and `/settings`: a sticky
 * `Paper` header carrying the actions, gradient `Tabs`, bordered `Paper` cards inside each panel.
 *
 * **Search-first, deliberately.** There is no roster here. The directory is the whole church, and a
 * screen that opens with thousands of rows invites scrolling for a person who would be found in two
 * keystrokes — and invites creating a duplicate when the scroll does not reach them. Both tabs
 * therefore start from a search box, and the Add tab keeps searching **as the name is typed**, so a
 * near-match surfaces before the record is created rather than after.
 *
 * ⚠️ **No delete, by instruction.** There is no remove action, and no active/inactive switch either:
 * deactivating is this system's nearest equivalent to deleting a person. `MemberService` exposes both
 * `delete` (inherited from `CrudService`) and `updateStatus`, and neither is called from this feature.
 *
 * ⚠️ **Identity is never written.** `userId`, `memberID`, `memberCode`, `typeID` and the Planning
 * Centre fields are shown read-only and cannot enter a payload. `memberFields.ts` is the contract and
 * explains each one; read it before adding a field here.
 *
 * ⓘ Search covers name and email. Whether it covers a cellphone number is not documented and could not
 * be confirmed from the SDK, so the empty state says as much rather than letting a phone search look
 * like "this person does not exist". See `_deploy/_newEndPoints/members-admin.md` §1.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IconAlertTriangle, IconInfoCircle, IconPlus, IconRefresh, IconSearch, IconUserPlus, IconUsers,
} from '@tabler/icons-react';
import {
  Alert, Avatar, Badge, Box, Button, Divider, Group, Paper, SearchInput, Select, Skeleton, Stack,
  Text, ThemeIcon, Title,
} from '@/components/ui';
import Tabs from '@/components/ui/navigation/Tabs';
import { CCMSDataTable, ListPagination, ROW_PAGE_SIZES } from '@/components';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useMaritalStatusService, useMemberService, useMemberTypeService,
  type MaritalStatus, type Member, type MemberType,
} from '@actschurch/ccms-sdk';
import { getInitialsFromName, resolveAvatarSrc } from '@/utils/avatar';
import MemberEditDrawer from './MemberEditDrawer';
import MemberForm from './MemberForm';
import {
  EMPTY_FORM, toWireDate, validateForm,
  type MemberFormValues,
} from './memberFields';

/** Enough to see whether the person is already there, without paging the directory. */
const SEARCH_LIMIT = 50;
/** Below this a search matches most of the church, so it is not run at all. */
const MIN_TERM = 2;

const fullNameOf = (m: Member) =>
  [m.firstName, m.middleName, m.lastName].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();

/** Reads through the `ResponseData` envelope, as elsewhere in this app. */
function unwrap<T>(res: unknown, fallback: T): T {
  const raw = (res as { data?: unknown })?.data ?? res;
  return (raw ?? fallback) as T;
}

export default function AdministrationMembersPage() {
  const memberService = useMemberService();
  const memberTypeService = useMemberTypeService();
  const maritalStatusService = useMaritalStatusService();

  const [tab, setTab] = useState<string | null>('find');
  const [maritalStatuses, setMaritalStatuses] = useState<MaritalStatus[]>([]);
  const [memberTypes, setMemberTypes] = useState<MemberType[]>([]);

  // ── Lookups ───────────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.allSettled([maritalStatusService.getAll(), memberTypeService.getAll()])
      .then(([statuses, types]) => {
        if (statuses.status === 'fulfilled') {
          setMaritalStatuses(unwrap<MaritalStatus[]>(statuses.value, []));
        } else {
          console.error('[Members] marital statuses failed to load:', statuses.reason);
        }
        if (types.status === 'fulfilled') {
          setMemberTypes(unwrap<MemberType[]>(types.value, []));
        } else {
          console.error('[Members] member types failed to load:', types.reason);
        }
      });
  }, [maritalStatusService, memberTypeService]);

  // ── Find ──────────────────────────────────────────────────────────────────
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Member[]>([]);
  const [searching, setSearching] = useState(false);
  /** Null until a search has actually run, so "no matches" is never shown before one has. */
  const [searchedFor, setSearchedFor] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(ROW_PAGE_SIZES[0] ?? 10);
  const [selected, setSelected] = useState<Member | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const runSearch = useCallback(async (raw: string) => {
    const q = raw.trim();
    if (q.length < MIN_TERM) { setResults([]); setSearchedFor(null); return; }
    setSearching(true);
    try {
      const res = await memberService.searchMembers(q, SEARCH_LIMIT);
      setResults(unwrap<Member[]>(res, []));
      setSearchedFor(q);
      setPage(1);
    } catch (e) {
      console.error('[Members] search failed:', e);
      setResults([]);
      setSearchedFor(q);
      toast.error('Search failed', e instanceof Error ? e.message : String(e));
    } finally {
      setSearching(false);
    }
  }, [memberService]);

  // Debounced, so a name typed at speed is one request rather than eight.
  useEffect(() => {
    const t = setTimeout(() => { runSearch(term); }, 350);
    return () => clearTimeout(t);
  }, [term, runSearch]);

  const paged = useMemo(
    () => results.slice((page - 1) * pageSize, page * pageSize),
    [results, page, pageSize],
  );

  // ⚠️ Clamps when the result set shrinks. Landing on page 4 of a 2-page list shows an empty table and
  // reads as "no matches", which is the same omission that blanked a list elsewhere in this app.
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(results.length / pageSize));
    if (page > lastPage) setPage(lastPage);
  }, [results.length, pageSize, page]);

  const openMember = (m: Member) => { setSelected(m); setDrawerOpen(true); };

  const columns: TableColumn<Member>[] = useMemo(() => [
    {
      key: 'name', header: 'Name', accessor: (m) => fullNameOf(m), sortable: true,
      render: (_, m) => (
        <Group gap="sm" wrap="nowrap">
          <Avatar src={resolveAvatarSrc(m.avatar)} radius="xl" size="sm">
            {getInitialsFromName(fullNameOf(m))}
          </Avatar>
          <Box style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} truncate>{fullNameOf(m) || 'Unnamed'}</Text>
            <Text size="xs" c="dimmed" truncate>{m.email || 'No email'}</Text>
          </Box>
        </Group>
      ),
    },
    { key: 'cellNumber', header: 'Cellphone', accessor: (m) => m.cellNumber ?? '—', sortable: true },
    {
      key: 'typeID', header: 'Type', accessor: (m) => m.typeID, sortable: true,
      render: (_, m) => {
        const name = memberTypes.find((t) => t.typeID === m.typeID)?.name;
        return name ? <Badge size="sm" variant="light">{name}</Badge> : '—';
      },
    },
    {
      key: 'isActive', header: 'Status', accessor: (m) => (m.isActive ? 'Active' : 'Not active'),
      sortable: true,
      render: (_, m) => (
        <Badge size="sm" variant="light" color={m.isActive ? 'teal' : 'gray'}>
          {m.isActive ? 'Active' : 'Not active'}
        </Badge>
      ),
    },
    {
      key: 'actions', header: '', accessor: (m) => m, sortable: false, align: 'center',
      render: (_, m) => (
        <Button variant="light" size="xs" onClick={(e) => { e.stopPropagation(); openMember(m); }}>
          View and edit
        </Button>
      ),
    },
  ], [memberTypes]);

  // ── Add ───────────────────────────────────────────────────────────────────
  const [addValues, setAddValues] = useState<MemberFormValues>(EMPTY_FORM);
  const [addErrors, setAddErrors] = useState<Partial<Record<keyof MemberFormValues, string>>>({});
  const [addTypeID, setAddTypeID] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /** Existing people who look like the one being typed in. */
  const [possible, setPossible] = useState<Member[]>([]);
  const possibleRef = useRef(0);

  /**
   * Live duplicate check while the new person is being typed.
   *
   * ⚠️ This is the point of the whole screen. Creating a second record for someone who already exists
   * splits their history across two members, and nothing downstream reconciles them. The check runs on
   * the name and the email, and its results are shown as **openable** rows so the admin can switch to
   * correcting the existing record instead.
   */
  useEffect(() => {
    const name = [addValues.firstName, addValues.lastName].filter(Boolean).join(' ').trim();
    const q = addValues.email.trim() || name;
    if (q.length < 3) { setPossible([]); return; }

    const seq = ++possibleRef.current;
    const t = setTimeout(async () => {
      try {
        const res = await memberService.searchMembers(q, 5);
        // Ignore a slow response that a newer keystroke has already superseded.
        if (seq !== possibleRef.current) return;
        setPossible(unwrap<Member[]>(res, []));
      } catch (e) {
        console.error('[Members] duplicate check failed:', e);
        if (seq === possibleRef.current) setPossible([]);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [addValues.firstName, addValues.lastName, addValues.email, memberService]);

  const resetAdd = () => {
    setAddValues(EMPTY_FORM);
    setAddErrors({});
    setAddTypeID(null);
    setPossible([]);
  };

  const handleCreate = async () => {
    const found = validateForm(addValues);
    setAddErrors(found);
    if (Object.keys(found).length > 0) return;
    if (!addTypeID) {
      toast.warning('Choose a member type', 'A new person needs a type before they can be created.');
      return;
    }

    setCreating(true);
    try {
      /**
       * ⚠️ The cast is unavoidable today. `MemberService` is `CrudService<Member, Member, …>`, so
       * `create` demands a **whole** `Member` including `memberID` and `userId` — both of which the
       * server generates. Inventing either would be far worse than the cast. The existing member
       * quick-add in the case wizard does the same thing.
       * A proper `CreateMemberRequest` is requested in `_deploy/_newEndPoints/members-admin.md` §2.
       */
      const draft = {
        firstName: addValues.firstName.trim(),
        middleName: addValues.middleName.trim() || null,
        lastName: addValues.lastName.trim() || null,
        dateOfBirth: toWireDate(addValues.dateOfBirth),
        gender: addValues.gender || null,
        email: addValues.email.trim(),
        cellNumber: addValues.cellNumber.trim() || null,
        altContact: addValues.altContact.trim() || null,
        telephone: addValues.telephone.trim() || null,
        typeID: Number(addTypeID),
        isMember: true,
        isActive: true,
        ...(addValues.maritalStatusID != null
          ? { maritalStatusID: addValues.maritalStatusID }
          : {}),
      };

      const res = await memberService.create(draft as unknown as Member);
      const created = unwrap<Member | null>(res, null);

      if (!created?.memberID) {
        // ⚠️ Not reported as success. Without an id there is nothing to open, and claiming a record was
        // created when the response cannot prove it is how a duplicate gets made on the second attempt.
        toast.warning(
          'Created, but not confirmed',
          'The server accepted the new member but did not return the record. Search for them before '
          + 'adding them again.',
        );
        return;
      }

      toast.success('Member added', `${fullNameOf(created) || 'The member'} was created.`);
      resetAdd();
      setTab('find');
      setTerm(created.email || fullNameOf(created));
      openMember(created);
    } catch (e) {
      console.error('[Members] create failed:', e);
      toast.error('Could not add', e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  };

  const typeOptions = memberTypes.map((t) => ({ value: String(t.typeID), label: t.name }));

  return (
    <Box p="md">
      <Paper
        withBorder
        radius="md"
        p="md"
        mb="md"
        style={{ position: 'sticky', top: 0, zIndex: 2 }}
      >
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="sm">
            <ThemeIcon variant="light" size="lg"><IconUsers size={20} /></ThemeIcon>
            <Box>
              <Title order={3}>Members</Title>
              <Text size="sm" c="dimmed">
                Find someone, check what is on record, and correct it. People are never removed here.
              </Text>
            </Box>
          </Group>
          <Group gap="xs">
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={() => runSearch(term)}
              disabled={term.trim().length < MIN_TERM || searching}
            >
              Search again
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => setTab('add')}
            >
              Add a member
            </Button>
          </Group>
        </Group>
      </Paper>

      <Tabs value={tab} onChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="find" leftSection={<IconSearch size={16} />}>Find a member</Tabs.Tab>
          <Tabs.Tab value="add" leftSection={<IconUserPlus size={16} />}>Add a member</Tabs.Tab>
        </Tabs.List>

        {/* ── Find ─────────────────────────────────────────────────────────── */}
        <Tabs.Panel value="find" pt="md">
          <Paper withBorder radius="md" p="md">
            <Group gap="sm" mb="xs">
              <ThemeIcon variant="light" size="sm"><IconSearch size={14} /></ThemeIcon>
              <Box>
                <Text fw={600} size="sm">Search the directory</Text>
                <Text size="xs" c="dimmed">
                  By name or email address. At least {MIN_TERM} characters.
                </Text>
              </Box>
            </Group>
            <Divider mb="md" />

            {/* ⚠️ This `SearchInput` hands back the **string**, not the event, unlike a plain TextInput. */}
            <SearchInput
              placeholder="Name or email address"
              value={term}
              onChange={setTerm}
              onClear={() => setTerm('')}
              mb="md"
            />

            {searching ? (
              <Stack gap="xs">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={44} radius="sm" />)}
              </Stack>
            ) : term.trim().length < MIN_TERM ? (
              <Alert type="info" icon={<IconInfoCircle size={16} />}>
                <Text size="sm">
                  Start typing a name or an email address. Nothing is listed until you search, because
                  the directory is the whole church.
                </Text>
              </Alert>
            ) : results.length === 0 && searchedFor ? (
              <Alert type="warning" icon={<IconAlertTriangle size={16} />}>
                <Text size="sm">
                  No one matches &ldquo;{searchedFor}&rdquo;. Search covers names and email addresses;
                  a cellphone number may not find anybody even when the person exists. Try their name
                  before adding them as new.
                </Text>
              </Alert>
            ) : (
              <>
                <Text size="sm" c="dimmed" mb="xs">
                  {results.length} {results.length === 1 ? 'person' : 'people'} found
                  {results.length === SEARCH_LIMIT && ' (showing the first ' + SEARCH_LIMIT + ')'}.
                </Text>
                <CCMSDataTable
                  data={paged}
                  columns={columns}
                  onRowClick={(m: Member) => openMember(m)}
                />
                <ListPagination
                  totalItems={results.length}
                  page={page}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={(n: number) => { setPageSize(n); setPage(1); }}
                />
              </>
            )}
          </Paper>
        </Tabs.Panel>

        {/* ── Add ──────────────────────────────────────────────────────────── */}
        <Tabs.Panel value="add" pt="md">
          <Stack gap="md">
            {possible.length > 0 && (
              <Alert type="warning" icon={<IconAlertTriangle size={16} />}>
                <Stack gap="xs">
                  <Text size="sm" fw={600}>
                    {possible.length === 1
                      ? 'Someone with a similar name is already on record'
                      : `${possible.length} people with similar details are already on record`}
                  </Text>
                  <Text size="xs">
                    Open one to correct their details instead of creating a second record for them.
                  </Text>
                  <Stack gap={6}>
                    {possible.map((m) => (
                      <Group key={m.memberID} justify="space-between" wrap="nowrap">
                        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                          <Avatar src={resolveAvatarSrc(m.avatar)} radius="xl" size="sm">
                            {getInitialsFromName(fullNameOf(m))}
                          </Avatar>
                          <Box style={{ minWidth: 0 }}>
                            <Text size="sm" fw={600} truncate>{fullNameOf(m)}</Text>
                            <Text size="xs" c="dimmed" truncate>
                              {[m.email, m.cellNumber].filter(Boolean).join(' · ') || 'No contact on record'}
                            </Text>
                          </Box>
                        </Group>
                        <Button size="xs" variant="light" onClick={() => openMember(m)}>
                          Open
                        </Button>
                      </Group>
                    ))}
                  </Stack>
                </Stack>
              </Alert>
            )}

            <Paper withBorder radius="md" p="md">
              <Group gap="sm" mb="xs">
                <ThemeIcon variant="light" size="sm"><IconUserPlus size={14} /></ThemeIcon>
                <Box>
                  <Text fw={600} size="sm">New member</Text>
                  <Text size="xs" c="dimmed">
                    The account link and member code are issued by the church system, not set here.
                  </Text>
                </Box>
              </Group>
              <Divider mb="md" />

              <Stack gap="md">
                <Select
                  label="Member type"
                  description="What this person is to Acts Care. Set once, and not editable afterwards."
                  required
                  data={typeOptions}
                  value={addTypeID}
                  onChange={setAddTypeID}
                  searchable
                  disabled={creating}
                />

                <MemberForm
                  values={addValues}
                  errors={addErrors}
                  maritalStatuses={maritalStatuses}
                  disabled={creating}
                  onChange={(patch) => setAddValues((prev) => ({ ...prev, ...patch }))}
                />

                <Group justify="flex-end">
                  <Button variant="subtle" onClick={resetAdd} disabled={creating}>Clear</Button>
                  <Button
                    leftSection={<IconUserPlus size={16} />}
                    onClick={handleCreate}
                    loading={creating}
                  >
                    Add member
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Stack>
        </Tabs.Panel>
      </Tabs>

      <MemberEditDrawer
        member={selected}
        maritalStatuses={maritalStatuses}
        memberTypes={memberTypes}
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSaved={(updated) => {
          setSelected(updated);
          // Keeps the row in step without re-running the search behind the drawer.
          setResults((prev) => prev.map((m) => (m.memberID === updated.memberID ? updated : m)));
        }}
      />
    </Box>
  );
}
